import * as React from "react";
import { useDropdownAlignment } from "../../../hooks/useDropdownAlignment";
import styled from "styled-components";
import BodySmall from "../typography/BodySmall";
import { Row } from "react-grid-system";
import Checkbox from "./Checkbox";
import { theme } from "../../../theme";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../icons/ChevronIcons";
import { VerticalSpacer } from "../Spacers";
import Divider from "../Divider";
import { useTranslation } from "react-i18next";

interface Props {
  options: { value: string; label: string }[];
  selectedOptions: string[];
  onChange: (selectedOptions: string[]) => void;
  onClickSelectAll?: () => void;
  title: string;
  fullWidth?: boolean;
  mode?: "filter" | "picker";
  disabled?: boolean;
}

const SelectContainer = styled.div<{
  $fullWidth: boolean;
  $disabled?: boolean;
}>`
  border-bottom: 1.25px solid ${theme.colors.grey40};
  padding: 0px 8px;
  height: 36px;
  box-sizing: border-box;
  color: ${theme.colors.text};
  font-size: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
  white-space: nowrap;
  gap: 8px;
  background-color: transparent;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:hover {
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    background-color: ${({ $disabled }) =>
      $disabled ? theme.colors.white : theme.colors.hover};
  }
`;

const OptionsContainer = styled.div<{
  $alignRight: boolean;
  $fullWidth?: boolean;
}>`
  position: absolute;
  top: calc(100% - 4px);
  ${({ $alignRight }) => ($alignRight ? "right: 0;" : "left: 0;")}
  z-index: 100;
  background-color: ${theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${theme.colors.grey40};
  box-shadow: ${theme.shadow.cardShadow};
  padding: 8px;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
`;

const OptionStyled = styled.div`
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

const MultiSelect: React.FC<Props> = ({
  options,
  selectedOptions,
  onChange,
  onClickSelectAll,
  title,
  fullWidth = false,
  mode = "filter",
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [showOptions, setShowOptions] = React.useState(false);
  const { panelRef, alignRight } = useDropdownAlignment(showOptions);

  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  const isFilter = mode === "filter";

  const onToggleOption = (option: string) => {
    const current = selectedOptions;
    if (current.includes(option)) {
      const filtered = current.filter(
        (selectedOption) => selectedOption !== option,
      );
      onChange(filtered);
    } else {
      const updatedFilter = [...current, option];
      onChange(updatedFilter);
    }
  };

  const onSelectAll = () => {
    onClickSelectAll?.();
  };

  const handleClickOutside = (event: any) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowOptions(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <SelectContainer $fullWidth={fullWidth} $disabled={disabled}>
        <Row
          justify="between"
          nogutter
          align="center"
          onClick={() => !disabled && setShowOptions(!showOptions)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <BodySmall fontweight={600}>{title}:</BodySmall>
            <BodySmall>
              {isFilter && selectedOptions.length === 0
                ? t("common.all")
                : `${selectedOptions.length} ${t(
                    "common.selected",
                  ).toLowerCase()}`}
            </BodySmall>
          </div>
          <VerticalSpacer spacing={0.5} />
          {showOptions ? <ChevronSmallUpIcon /> : <ChevronSmallDownIcon />}
        </Row>
        {showOptions && (
          <OptionsContainer
            ref={panelRef}
            $alignRight={alignRight}
            $fullWidth={fullWidth}
          >
            {!isFilter && (
              <>
                <OptionStyled>
                  <Checkbox
                    checked={
                      options.length > 0 &&
                      options.every((option) =>
                        selectedOptions.includes(option.value),
                      )
                    }
                    title={t("common.all")}
                    onChange={onSelectAll}
                  />
                </OptionStyled>
                <Divider />
              </>
            )}
            {options.map((option) => (
              <OptionStyled key={option.value}>
                <Checkbox
                  checked={selectedOptions.includes(option.value)}
                  onChange={() => onToggleOption(option.value)}
                  title={t(option.label)}
                />
              </OptionStyled>
            ))}
          </OptionsContainer>
        )}
      </SelectContainer>
    </div>
  );
};

export default MultiSelect;
